// src/components/DisciplineWrittenReportPanel.js
//
// T5 / D-23 (2026-10-16) - the written report of the decision disposing of a discipline case, due within 30 days to the
// Local Chairperson and the Secretary of the Local (SMART constitution Art. 21B Sec. 44). src\data\writtenDecisionReport.js
// decides the clock and checks each record; this file draws and writes. Records are appended to the case's
// writtenReports[] with arrayUnion - never rewritten - by an officer of the case; the rules refuse the member's own
// account (ownerOpenEdit) and the disciplineAuditHistory trigger records every change.
import React, { useState } from 'react';
import { db, auth } from '../firebase';
import { arrayUnion, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { RECIPIENTS, METHODS, SECTION_44_WORDS, SECTION_44_CITE, planWrittenReport, writtenReportStatus } from '../data/writtenDecisionReport';

const C = { line: '#2E4A6B', dim: '#8AA4BF', text: '#DCE7F2', ok: '#5FB878', bad: '#E06C6C', accent: '#E8A020' };
const todayISO = () => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); };

function isOfficer(user) {
  return !!user && (user.isAdmin || user.role === 'admin' || user.role === 'local_chair' || user.role === 'general_chair');
}

export default function DisciplineWrittenReportPanel({ disciplineCase, user }) {
  const k = disciplineCase || {};
  const [form, setForm] = useState({ to: 'local_chair', toName: '', on: todayISO(), method: 'certified_mail', documentName: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const st = writtenReportStatus(k, todayISO());
  if (!k.id || !st.applies) return null;
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const docs = (k.documents || []).map(d => d.name).filter(Boolean);

  const save = async () => {
    const r = planWrittenReport({ ...form, today: todayISO() }, k);
    if (!r.ok) { setMsg({ ok: false, text: r.errors.join(' ') }); return; }
    setBusy(true); setMsg(null);
    try {
      const stored = { ...r.record, recordedBy: (user && (user.name || user.email)) || '', recordedByUid: (auth.currentUser && auth.currentUser.uid) || '', recordedAt: new Date().toISOString() };
      await updateDoc(doc(db, 'disciplineCases', k.id), {
        writtenReports: arrayUnion(stored), updatedAt: serverTimestamp(),
        updatedBy: (auth.currentUser && auth.currentUser.uid) || '', _auditActor: (user && user.email) || 'unknown',
        _auditReason: 'Written decision furnished to ' + RECIPIENTS[r.record.to] + ' ' + r.record.on,
      });
      setForm(p => ({ ...p, toName: '', documentName: '' }));
      setMsg({ ok: true, text: 'Recorded.' });
    } catch (e) { setMsg({ ok: false, text: (e && e.message) || 'Not recorded.' }); }
    setBusy(false);
  };

  return (
    <div style={{ border: '1px solid ' + C.line, padding: '10px 14px', margin: '12px 0' }}>
      <div style={{ color: C.text, fontWeight: 700, fontSize: 13 }}>The written decision - within 30 days</div>
      <div style={{ color: C.dim, fontSize: 11, margin: '2px 0 8px' }}>"{SECTION_44_WORDS}" ({SECTION_44_CITE})</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: st.overdue || st.late.length ? C.bad : st.owed.length ? C.accent : C.ok, marginBottom: 6 }}>{st.line}</div>
      <div style={{ fontSize: 11, color: C.dim, marginBottom: 6 }}>The case was resolved on {st.decidedOn}. The platform keeps no Secretary of the Local on record, so it cannot send the report; record each copy here when it goes.</div>
      {st.rows.map((r, i) => (
        <div key={i} style={{ fontSize: 12, color: C.text, padding: '3px 0', borderBottom: '1px dotted ' + C.line }}>
          {r.on} - to {RECIPIENTS[r.to]} ({r.toName}) by {METHODS[r.method]}{r.documentName ? ' - ' + r.documentName : ''}{r.on > st.dueOn ? ' - LATE' : ''}
          <span style={{ color: C.dim, fontSize: 11 }}> - recorded by {r.recordedBy}</span>
        </div>
      ))}
      {isOfficer(user) ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
          <select aria-label="Who the report goes to" className="form-select" value={form.to} onChange={e => set('to', e.target.value)}>{Object.entries(RECIPIENTS).map(([v, l]) => <option key={v} value={v}>to {l}</option>)}</select>
          <input aria-label="Their name" className="form-input" placeholder="Their name" value={form.toName} onChange={e => set('toName', e.target.value)} />
          <input aria-label="Date" className="form-input" type="date" value={form.on} onChange={e => set('on', e.target.value)} title="The date it was furnished" />
          <select aria-label="How the report is sent" className="form-select" value={form.method} onChange={e => set('method', e.target.value)}>{Object.entries(METHODS).map(([v, l]) => <option key={v} value={v}>by {l}</option>)}</select>
          <select aria-label="Document name" className="form-select" style={{ gridColumn: '1 / span 2' }} value={form.documentName} onChange={e => set('documentName', e.target.value)}><option value="">The written decision on the case (optional)…</option>{docs.map(n => <option key={n} value={n}>{n}</option>)}</select>
          {msg ? <div style={{ gridColumn: '1 / span 2', fontSize: 12, color: msg.ok ? C.ok : C.bad }}>{msg.text}</div> : null}
          <button className="btn btn-primary" disabled={busy} onClick={save}>Record that it was furnished</button>
        </div>
      ) : null}
    </div>
  );
}
